import { ArrowUpRight, Clock, Search } from "lucide-react";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useData } from "vike-react/useData";
import { Input } from "@/components/ui/input";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import { formatPublicationDate } from "@/lib/date";
import ArticlePlaceholder from "@/components/blog/ArticlePlaceholder";
import ArticleImage from "@/components/blog/ArticleImage";
import { trackEvent } from "@/lib/analytics";
import type { ArticleSummary } from "@/server/db";
import type { BlogListData } from "./+data";

const CtaBanner = lazy(() => import("@/components/landing/CtaBanner"));

const CtaFallback = () => (
  <div className="h-48 w-full animate-pulse rounded-md border border-border/60 bg-surface-elevated/40" />
);

const BREADCRUMB_ITEMS = [
  { name: "Beranda", path: "/" },
  { name: "Blog", path: "/blog" },
];

const ALL_CATEGORIES_LABEL = "Semua";
const LIST_LOCATION = "blog_list";
const SEARCH_DEBOUNCE_MS = 500;

export default function Page() {
  const { articles } = useData<BlogListData>();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORIES_LABEL);

  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(articles.map((a) => a.article_category).filter((c): c is string => Boolean(c))),
    );
    return [ALL_CATEGORIES_LABEL, ...unique];
  }, [articles]);

  const filteredArticles = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return articles.filter((article) => {
      const matchesCategory =
        activeCategory === ALL_CATEGORIES_LABEL ||
        article.article_category === activeCategory;
      if (!matchesCategory) return false;
      if (!normalizedQuery) return true;
      return (
        article.article_title.toLowerCase().includes(normalizedQuery) ||
        (article.article_excerpt ?? "").toLowerCase().includes(normalizedQuery)
      );
    });
  }, [articles, searchQuery, activeCategory]);

  // Fire GA4 `search` after the user pauses typing for SEARCH_DEBOUNCE_MS.
  // Skip empty queries — we only care about deliberate searches.
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    const timer = window.setTimeout(() => {
      trackEvent("search", {
        search_term: trimmed,
        result_count: filteredArticles.length,
        active_category: activeCategory,
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchQuery, activeCategory, filteredArticles.length]);

  const handleArticleClick = (article: ArticleSummary) => () =>
    trackEvent("select_content", {
      content_type: "article",
      item_id: article.article_slug,
      item_name: article.article_title,
      item_category: article.article_category ?? undefined,
      list_location: LIST_LOCATION,
    });

  const [featuredArticle, ...restArticles] = filteredArticles;

  return (
    <>
      <section className="relative isolate overflow-hidden bg-gradient-hero pb-16 pt-16 md:pt-24">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        <div className="container-narrow relative">
          <Breadcrumbs items={BREADCRUMB_ITEMS} className="mb-8" />
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-mono text-xs uppercase tracking-widest text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              Insights & Strategi
            </div>
            <h1 className="heading-display text-4xl text-foreground sm:text-5xl md:text-6xl">
              Playbook <span className="text-primary">Auto-Care</span> untuk Praktisi.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Studi kasus, strategi profit, dan SOP operasional dari mitra Scudetto —
              disusun untuk Anda yang serius membangun bisnis garasi.
            </p>
          </div>

          <div className="mx-auto mt-12 max-w-3xl">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Cari artikel, topik, atau studi kasus…"
                className="h-12 border-border bg-surface-elevated pl-11 text-base"
                aria-label="Cari artikel"
              />
            </div>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {categories.map((category) => {
                const isActive = category === activeCategory;
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setActiveCategory(category)}
                    className={`text-mono text-xs uppercase tracking-wider transition-all rounded-sm px-3 py-1.5 border ${
                      isActive
                        ? "border-primary/50 bg-primary/15 text-primary"
                        : "border-border bg-surface-elevated text-muted-foreground hover:border-primary/30 hover:text-foreground"
                    }`}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface-elevated py-20 md:py-24">
        <div className="container-narrow">
          {filteredArticles.length === 0 ? (
            <div className="mx-auto max-w-md rounded-md border border-border bg-gradient-card p-10 text-center">
              <p className="text-mono text-xs uppercase tracking-widest text-muted-foreground">
                // No results
              </p>
              <h2 className="mt-3 text-xl font-bold text-foreground">
                Belum ada artikel yang cocok.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Coba ubah kata kunci atau pilih kategori lain.
              </p>
            </div>
          ) : (
            <>
              {featuredArticle && (
                <a
                  href={`/blog/${featuredArticle.article_slug}`}
                  className="group grid grid-cols-1 gap-8 overflow-hidden rounded-md border border-border bg-gradient-card shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-elevated md:grid-cols-2"
                  onClick={handleArticleClick(featuredArticle)}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-surface-raised md:aspect-auto">
                    {featuredArticle.article_thumbnail_url || featuredArticle.article_thumbnail_mobile_url ? (
                      <ArticleImage
                        src={featuredArticle.article_thumbnail_url}
                        mobileSrc={featuredArticle.article_thumbnail_mobile_url}
                        sizes="(min-width: 768px) 50vw, 100vw"
                        alt={featuredArticle.article_title}
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        width={1200}
                        height={750}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <ArticlePlaceholder
                        slug={featuredArticle.article_slug}
                        title={featuredArticle.article_title}
                        className="transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                  </div>
                  <div className="flex flex-col justify-center p-8 md:p-10">
                    <div className="flex items-center gap-3 text-mono text-xs uppercase tracking-wider text-muted-foreground">
                      <span className="rounded-sm bg-primary/15 px-2 py-0.5 text-primary">
                        Featured
                      </span>
                      {featuredArticle.article_category && (
                        <span className="rounded-sm bg-surface-raised px-2 py-0.5">
                          {featuredArticle.article_category}
                        </span>
                      )}
                      {featuredArticle.read_minutes > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {featuredArticle.read_minutes} min
                        </span>
                      )}
                    </div>

                    <h2 className="heading-display mt-5 text-3xl text-foreground transition-colors group-hover:text-primary md:text-4xl">
                      {featuredArticle.article_title}
                    </h2>

                    {featuredArticle.article_excerpt && (
                      <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                        {featuredArticle.article_excerpt}
                      </p>
                    )}

                    <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-5">
                      <div className="text-mono text-xs uppercase tracking-wider text-muted-foreground">
                        {featuredArticle.article_author_name ?? "Editorial"} ·{" "}
                        {featuredArticle.published_at && (
                          <time dateTime={featuredArticle.published_at}>
                            {formatPublicationDate(featuredArticle.published_at)}
                          </time>
                        )}
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary transition-all group-hover:gap-2">
                        Baca <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </a>
              )}

              {restArticles.length > 0 && (
                <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {restArticles.map((article) => (
                    <article
                      key={article.id}
                      className="group relative flex flex-col overflow-hidden rounded-md border border-border bg-gradient-card shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-elevated"
                    >
                      <a
                        href={`/blog/${article.article_slug}`}
                        className="block aspect-[16/10] overflow-hidden bg-surface-raised"
                        onClick={handleArticleClick(article)}
                      >
                        {article.article_thumbnail_url || article.article_thumbnail_mobile_url ? (
                          <ArticleImage
                            src={article.article_thumbnail_url}
                            mobileSrc={article.article_thumbnail_mobile_url}
                            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                            alt={article.article_title}
                            loading="lazy"
                            decoding="async"
                            width={800}
                            height={500}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <ArticlePlaceholder
                            slug={article.article_slug}
                            title={article.article_title}
                            className="transition-transform duration-500 group-hover:scale-105"
                          />
                        )}
                      </a>

                      <div className="flex flex-1 flex-col p-6">
                        <div className="flex items-center gap-3 text-mono text-xs uppercase tracking-wider text-muted-foreground">
                          {article.article_category && (
                            <span className="rounded-sm bg-primary/15 px-2 py-0.5 text-primary">
                              {article.article_category}
                            </span>
                          )}
                          {article.read_minutes > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {article.read_minutes} min
                            </span>
                          )}
                        </div>

                        <h3 className="mt-4 text-lg font-bold leading-tight text-foreground transition-colors group-hover:text-primary">
                          <a
                            href={`/blog/${article.article_slug}`}
                            onClick={handleArticleClick(article)}
                          >
                            {article.article_title}
                          </a>
                        </h3>

                        {article.article_excerpt && (
                          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                            {article.article_excerpt}
                          </p>
                        )}

                        <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4">
                          {article.published_at && (
                            <time
                              dateTime={article.published_at}
                              className="text-xs text-muted-foreground"
                            >
                              {formatPublicationDate(article.published_at)}
                            </time>
                          )}
                          <a
                            href={`/blog/${article.article_slug}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary transition-all hover:gap-2"
                            onClick={handleArticleClick(article)}
                          >
                            Baca <ArrowUpRight className="h-3.5 w-3.5" />
                          </a>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Suspense fallback={<CtaFallback />}>
        <CtaBanner variant="section" />
      </Suspense>
    </>
  );
}
